import { Link } from "react-router-dom";
import { useFetch } from "../lib/useFetch";

interface DashboardData {
  participants: { total: number; onboarded: number; pipeline: number };
  shiftsToday: number;
  pendingTimesheets: number;
  fundingWarnings: number;
  openIncidents: number;
  expiringDocuments: number;
  urgentNotes: { id: number; participantId: number; participantName: string; authorName: string; body: string; createdAt: string }[];
}

export default function Dashboard() {
  const { data: d, loading } = useFetch<DashboardData>("/v1/dashboard");
  if (loading || !d) return <div className="space-y-4"><div className="skeleton h-8 w-48" /><div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{[...Array(5)].map((_, i) => <div key={i} className="skeleton h-24" />)}</div><div className="skeleton h-40" /></div>;

  const cards = [
    { label: "Participants", value: d.participants.total, sub: `${d.participants.onboarded} onboarded · ${d.participants.pipeline} in pipeline`, to: "/participants" },
    { label: "Shifts today", value: d.shiftsToday, sub: "on the roster", to: "/roster" },
    { label: "Awaiting approval", value: d.pendingTimesheets, sub: "timesheet entries", to: "/billing", warn: d.pendingTimesheets > 0 },
    { label: "Funding alerts", value: d.fundingWarnings, sub: "funds above 80%", to: "/participants", warn: d.fundingWarnings > 0 },
    { label: "Open incidents", value: d.openIncidents, sub: "in the register", to: "/quality", warn: d.openIncidents > 0 },
    { label: "Expiring documents", value: d.expiringDocuments, sub: "within 30 days", to: "/reports", warn: d.expiringDocuments > 0 },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-ink">Dashboard</h1>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className="card p-4 hover:border-teal-300 transition-colors">
            <p className="text-sm text-mut">{c.label}</p>
            <p className={`text-3xl font-semibold ${c.warn ? "text-amber-600" : "text-ink"}`}>{c.value}</p>
            <p className="text-xs text-faint mt-1">{c.sub}</p>
          </Link>
        ))}
      </div>

      {d.urgentNotes.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-medium text-ink">Urgent case notes — last 7 days</h2>
          {d.urgentNotes.map((n) => (
            <Link key={n.id} to={`/participants/${n.participantId}`}
              className="block card border-red-200 p-4 hover:border-red-300">
              <div className="flex items-center gap-2 mb-1">
                <span className="chip bg-red-100 text-red-700">urgent</span>
                <span className="text-sm font-medium text-ink">{n.participantName}</span>
                <span className="text-xs text-faint">by {n.authorName}</span>
              </div>
              <p className="text-sm text-body">{n.body}…</p>
            </Link>
          ))}
        </section>
      )}
    </div>
  );
}
